/**
 * JSON Import Sidebar Component
 * Inline JSON editor with tabs (not a modal)
 */

import { useState, useEffect, useRef } from 'react'
import { autoCleanAndValidate, type CleanJSONResult } from '../../lib/utils/jsonCleaner'
import { JsonEditor } from 'json-edit-react'
import './JSONImportSidebar.css'

interface JSONImportSidebarProps {
  onImport: (data: any) => void
  error?: string
  setError: (error: string) => void
}

const MIN_WIDTH = 300
const MAX_WIDTH = 800
const DEFAULT_WIDTH = 400

// Simple collapsible JSON tree component
interface JSONTreeProps {
  data: any
  name?: string
  level?: number
}

function JSONTree({ data, name, level = 0 }: JSONTreeProps) {
  const [isCollapsed, setIsCollapsed] = useState(level > 2)

  const indent = level * 20

  if (data === null) {
    return (
      <div style={{ marginLeft: indent }} className="json-tree-item">
        {name && <span className="json-key">{name}: </span>}
        <span className="json-null">null</span>
      </div>
    )
  }

  if (typeof data !== 'object') {
    const type = typeof data
    return (
      <div style={{ marginLeft: indent }} className="json-tree-item">
        {name && <span className="json-key">{name}: </span>}
        <span className={`json-${type}`}>
          {type === 'string' ? `"${data}"` : String(data)}
        </span>
      </div>
    )
  }

  const isArray = Array.isArray(data)
  const entries = isArray ? data : Object.entries(data)
  const isEmpty = entries.length === 0

  return (
    <div style={{ marginLeft: indent }} className="json-tree-node">
      <div className="json-tree-header" onClick={() => !isEmpty && setIsCollapsed(!isCollapsed)}>
        {!isEmpty && (
          <span className="json-tree-toggle">{isCollapsed ? '▶' : '▼'}</span>
        )}
        {name && <span className="json-key">{name}: </span>}
        <span className="json-bracket">
          {isArray ? '[' : '{'}
          {isEmpty && (isArray ? ']' : '}')}
          {!isEmpty && isCollapsed && (
            <span className="json-collapsed-indicator">
              ... {entries.length} {isArray ? 'items' : 'properties'}
            </span>
          )}
        </span>
      </div>
      {!isCollapsed && !isEmpty && (
        <div className="json-tree-children">
          {isArray
            ? data.map((item: any, index: number) => (
                <JSONTree key={index} data={item} name={`[${index}]`} level={level + 1} />
              ))
            : entries.map(([key, value]: [string, any]) => (
                <JSONTree key={key} data={value} name={key} level={level + 1} />
              ))}
        </div>
      )}
      {!isCollapsed && !isEmpty && (
        <div style={{ marginLeft: indent }} className="json-bracket">
          {isArray ? ']' : '}'}
        </div>
      )}
    </div>
  )
}

export default function JSONImportSidebar({ onImport, error, setError }: JSONImportSidebarProps) {
  const [rawJSON, setRawJSON] = useState('')
  const [viewMode, setViewMode] = useState<'raw' | 'tree' | 'editor'>('raw')
  const [cleanResult, setCleanResult] = useState<CleanJSONResult | null>(null)
  const [showAutoCleaned, setShowAutoCleaned] = useState(false)
  const [editableData, setEditableData] = useState<any>(null)

  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('jsonSidebarWidth')
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  console.log('🔷 [JSONImportSidebar] Rendered, viewMode:', viewMode, 'width:', sidebarWidth)

  // Auto-clean and validate when raw JSON changes
  useEffect(() => {
    if (rawJSON.trim()) {
      console.log('🔍 [JSONImportSidebar] Processing JSON input...')
      const result = autoCleanAndValidate(rawJSON)
      console.log('🔍 [JSONImportSidebar] Validation result:', result)
      setCleanResult(result)

      // Auto-switch to editor view if valid
      if (result.success) {
        console.log('✅ [JSONImportSidebar] JSON valid, switching to editor view')
        setEditableData(result.parsed)
        setViewMode('editor')
        setShowAutoCleaned(!!result.wasEscaped)
        setError('')
      } else {
        console.error('❌ [JSONImportSidebar] JSON validation failed:', result.error)
        setEditableData(null)
        setError(result.error || 'Invalid JSON')
      }
    } else {
      setCleanResult(null)
      setShowAutoCleaned(false)
      setEditableData(null)
      setError('')
    }
  }, [rawJSON, setError])

  const handleImport = () => {
    console.log('🔵 [JSONImportSidebar] Import button clicked')
    console.log('🔵 [JSONImportSidebar] Current view mode:', viewMode)
    console.log('🔵 [JSONImportSidebar] Clean result:', cleanResult)
    console.log('🔵 [JSONImportSidebar] Editable data:', editableData)

    // Use editableData if in editor mode (may have been edited), otherwise use cleanResult
    const dataToImport = viewMode === 'editor' && editableData ? editableData : cleanResult?.parsed

    if (dataToImport) {
      console.log('✅ [JSONImportSidebar] Validation passed, calling onImport')
      console.log('📦 [JSONImportSidebar] Data to import:', dataToImport)
      console.log('📊 [JSONImportSidebar] Nodes count:', dataToImport.nodes?.length)
      console.log('🔗 [JSONImportSidebar] Edges count:', dataToImport.edges?.length)

      onImport(dataToImport)

      // Clear the form after successful import
      setRawJSON('')
      setCleanResult(null)
      setEditableData(null)
      setShowAutoCleaned(false)
      setViewMode('raw')
      setError('')

      console.log('✅ [JSONImportSidebar] Import completed')
    } else {
      console.error('❌ [JSONImportSidebar] Import failed - no valid data')
      setError('No valid data to import')
    }
  }

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      setRawJSON(text)
    } catch (error) {
      console.error('Failed to read clipboard:', error)
      setError('Failed to read clipboard')
    }
  }

  // Resize handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = e.clientX
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth)
        localStorage.setItem('jsonSidebarWidth', newWidth.toString())
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  return (
    <div
      ref={sidebarRef}
      className="json-import-sidebar"
      style={{ width: `${sidebarWidth}px` }}
    >
      <h3>Import Diagram</h3>

      {/* Tabs */}
      <div className="json-sidebar-tabs">
        <button
          className={viewMode === 'raw' ? 'active' : ''}
          onClick={() => setViewMode('raw')}
        >
          📝 Raw
        </button>
        <button
          className={viewMode === 'tree' ? 'active' : ''}
          onClick={() => setViewMode('tree')}
          disabled={!cleanResult?.success}
        >
          🌲 Tree
        </button>
        <button
          className={viewMode === 'editor' ? 'active' : ''}
          onClick={() => setViewMode('editor')}
          disabled={!editableData}
        >
          ✏️ Editor
        </button>
      </div>

      {/* Paste button */}
      <button className="paste-button-sidebar" onClick={handlePaste} title="Paste from clipboard">
        📋 Paste from Clipboard
      </button>

      {/* Auto-cleaned notice */}
      {showAutoCleaned && cleanResult?.success && (
        <div className="json-auto-cleaned-notice-sidebar">
          ✨ Escaped JSON auto-cleaned!
        </div>
      )}

      {/* Content area */}
      <div className="json-sidebar-content">
        {viewMode === 'raw' ? (
          <textarea
            className="json-sidebar-textarea"
            value={rawJSON}
            onChange={(e) => setRawJSON(e.target.value)}
            placeholder={`Paste your diagram JSON here...\n\nSupports:\n• Regular JSON\n• Escaped JSON (\\n, \\", etc.)\n• Auto-cleaning and validation`}
            spellCheck={false}
          />
        ) : viewMode === 'tree' && cleanResult?.success ? (
          <div className="json-tree-view-sidebar">
            <JSONTree data={cleanResult.parsed} />
          </div>
        ) : viewMode === 'editor' && editableData ? (
          <div className="json-editor-view-sidebar">
            <JsonEditor
              data={editableData}
              setData={setEditableData}
              rootName="diagram"
              collapse={2}
              enableClipboard={true}
              restrictEdit={false}
              restrictDelete={false}
              restrictAdd={false}
            />
          </div>
        ) : (
          <div className="json-placeholder-sidebar">
            {!cleanResult?.success
              ? 'Paste JSON in Raw tab'
              : 'Loading...'}
          </div>
        )}
      </div>

      {/* Validation info */}
      {cleanResult?.success && (
        <div className="json-validation-info-sidebar">
          <span>✅ Valid</span>
          <span>• {cleanResult.parsed.nodes?.length || 0} nodes</span>
          <span>• {cleanResult.parsed.edges?.length || 0} edges</span>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="json-error-sidebar">
          <strong>❌ Error:</strong> {error}
        </div>
      )}

      {/* Import button */}
      <button
        onClick={handleImport}
        className="import-button-sidebar"
        disabled={!cleanResult?.success && !editableData}
      >
        📥 Import to Current Canvas
      </button>

      {/* Resize handle */}
      <div
        className="sidebar-resize-handle"
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      />
    </div>
  )
}
